import React, { useEffect } from "react";
import { IconButton, Tooltip, Checkbox, CircularProgress } from "@mui/material";
import { Delete, Save, CheckCircle } from "@mui/icons-material";
import { Edit } from "@mui/icons-material";
import useAttributeValueLogic from "../../../../../hooks/variants/useAttributeValueLogic";
import useProductConfiguration from "../../../../../hooks/useProductConfiguration";

const AttributeValuesSection = ({ attributes = [] }) => {
  const {
    attributeValues,
    initializeValues,
    addValue,
    changeValue,
    saveValue,
    deleteValue,
    updateValue,
    deleteSavedValue,
    toggleSelected,
    selectAllForAttr,
    selectAllGlobal,
    getAttrStats,
    isAttributeValueSelected,
    getSelectedAttributeValues,
    allCounts,
    allChecked,
    allIndeterminate,
    isDeletingValue,
  } = useAttributeValueLogic(attributes);

  const { selectedAttributes, setAttributeValues } = useProductConfiguration();

  // Local state to track which saved value is being edited
  const [editState, setEditState] = React.useState({});

  // Note: initializeValues() is already handled by the hook's useEffect with [selectedAttributes]
  // No need for duplicate useEffect here

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between my-4">
        <div>
          <h2 className="text-xl font-semibold">Step 2: Attribute Values</h2>
          <p className="text-gray-600">
            Add values for each attribute. Each combination creates a unique
            product SKU.
          </p>
        </div>

        {/* Global Select All */}
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={allChecked}
            indeterminate={allIndeterminate}
            onChange={(e) => selectAllGlobal(e.target.checked)}
            size="small"
          />
          <span className="text-gray-700">
            Select all ({allCounts.selected}/{allCounts.total})
          </span>
        </label>
      </div>

      {selectedAttributes.length === 0 ? (
        <div className="text-gray-500">No attributes selected.</div>
      ) : (
        selectedAttributes.map((attr, attrIdx) => {
          const { list, selectedCount, total, checked, indeterminate } =
            getAttrStats(attr.attribute_name);

          return (
            <div
              key={attr.attribute_id + attrIdx}
              className="border rounded-lg p-5 bg-white shadow-sm transition hover:shadow-md mb-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-medium text-lg">{attr.attribute_name}</h3>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={(e) =>
                        selectAllForAttr(attr.attribute_name, e.target.checked)
                      }
                      size="small"
                      disabled={total === 0}
                    />
                    <span className="text-gray-700">
                      Select all ({selectedCount}/{total})
                    </span>
                  </label>

                  <button
                    onClick={() => addValue(attr.attribute_slug)}
                    className="px-3 py-1 text-sm bg-black text-white rounded-md hover:opacity-90"
                  >
                    + Add Value
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(attributeValues[attr.attribute_slug] || []).map(
                  (val, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-gray-50 rounded-md p-2 border"
                    >
                      <Checkbox
                        checked={
                          val.attribute_value_id
                            ? isAttributeValueSelected(
                                attr.attribute_name,
                                val.attribute_value_id
                              )
                            : val.selected || false
                        }
                        disabled={val.saving || !val.attribute_value_id}
                        onChange={(e) => {
                          console.log("Checkbox changed:", {
                            attributeName: attr.attribute_name,
                            value: val,
                            checked: e.target.checked,
                            hasId: !!val.attribute_value_id,
                          });

                          // For saved values, use the selection system
                          if (val.attribute_value_id && !val.saving) {
                            toggleSelected(attr.attribute_name, val);
                          } else if (!val.attribute_value_id) {
                            // For new unsaved values, use local state
                            setAttributeValues((prev) => {
                              const copy = { ...prev };
                              copy[attr.attribute_slug] = copy[
                                attr.attribute_slug
                              ].map((v, i) =>
                                i === idx
                                  ? { ...v, selected: e.target.checked }
                                  : v
                              );
                              return copy;
                            });
                          }
                        }}
                        size="small"
                      />

                      <input
                        placeholder={`Enter ${attr.attribute_name}...`}
                        type="text"
                        onChange={(e) =>
                          changeValue(attr.attribute_slug, idx, e.target.value)
                        }
                        value={
                          val.name ||
                          val.attribute_value ||
                          val.attribute_value_name ||
                          ""
                        }
                        className="flex-1 h-9 rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-black"
                        disabled={
                          val.attribute_value_id &&
                          !editState[`${attr.attribute_slug}-${idx}`]
                        }
                        readOnly={
                          val.attribute_value_id &&
                          !editState[`${attr.attribute_slug}-${idx}`]
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !val.attribute_value_id)
                            saveValue(
                              attr.attribute_slug,
                              idx,
                              attr.attribute_id
                            );
                          if (
                            e.key === "Enter" &&
                            val.attribute_value_id &&
                            editState[`${attr.attribute_slug}-${idx}`]
                          ) {
                            updateValue(
                              attr.attribute_slug,
                              idx,
                              attr.attribute_id
                            );
                            setEditState((prev) => ({
                              ...prev,
                              [`${attr.attribute_slug}-${idx}`]: false,
                            }));
                          }
                        }}
                      />

                      {val.attribute_value_id ? (
                        // Saved value: show edit/delete/update controls
                        <>
                          {editState[`${attr.attribute_slug}-${idx}`] ? (
                            <>
                              <Tooltip title="Update">
                                <IconButton
                                  color="primary"
                                  onClick={() => {
                                    updateValue(
                                      attr.attribute_slug,
                                      idx,
                                      attr.attribute_id
                                    );
                                    setEditState((prev) => ({
                                      ...prev,
                                      [`${attr.attribute_slug}-${idx}`]: false,
                                    }));
                                  }}
                                  disabled={
                                    !(
                                      val.name ||
                                      val.attribute_value ||
                                      val.attribute_value_name
                                    )
                                  }
                                >
                                  <Save fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  onClick={() =>
                                    setEditState((prev) => ({
                                      ...prev,
                                      [`${attr.attribute_slug}-${idx}`]: false,
                                    }))
                                  }
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Saved - Click to Edit">
                                <IconButton
                                  color="success"
                                  onClick={() =>
                                    setEditState((prev) => ({
                                      ...prev,
                                      [`${attr.attribute_slug}-${idx}`]: true,
                                    }))
                                  }
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    deleteSavedValue(
                                      attr.attribute_slug,
                                      idx,
                                      attr.attribute_id
                                    )
                                  }
                                  disabled={isDeletingValue(
                                    val.attribute_value_id
                                  )}
                                >
                                  {isDeletingValue(val.attribute_value_id) ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <Delete fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </>
                      ) : val.saving ? (
                        // Value is being saved
                        <Tooltip title="Saving...">
                          <IconButton disabled>
                            <CircularProgress size={20} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        // New value being added
                        <>
                          <Tooltip title="Save">
                            <IconButton
                              color="primary"
                              onClick={() =>
                                saveValue(
                                  attr.attribute_slug,
                                  idx,
                                  attr.attribute_id
                                )
                              }
                              disabled={!val.name || val.name.trim() === ""}
                            >
                              <Save fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() =>
                                deleteValue(attr.attribute_slug, idx)
                              }
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const AttributeValues = () => (
  <div className="px-4">
    <AttributeValuesSection attributes={["Color", "Size"]} />
  </div>
);

export default AttributeValues;
